import useSWR from "swr";
import fetcher from "../utils/fetch";
import { RelativeUrl } from "../utils/environment";
import { Batch, BatchStatus, BatchServerResponse, BatchType } from "../types/batch";
import { useMemo } from "react";
import { snakeToCamel } from "../utils/case-conversions";
export const BatchUrl = "batches";

export const POLL_INTERVAL_MILLIS = 2000;

export interface BatchInfo {
  ids: number[];
  pollIntervalMillis: number;
}

export interface UseBatchesProps {
  themeId?: number;
  conceptId?: number;
  isCompleted?: boolean;
  ids?: number[];
  brandId?: number;
  type?: BatchType;
  creatorId?: number;
  variationSetIds?: number[];
  adAssetGroupIds?: number[];
  pollingIntervalMillis?: number;
}

export const isBatchComplete = (status: BatchStatus) => {
  return status === BatchStatus.Completed || status === BatchStatus.Failed || status === BatchStatus.PartialSuccess;
};

export const isBatchPending = (status: BatchStatus) => !isBatchComplete(status);

function parseInfo(info: string | Record<string, string>) {
  if (typeof info !== "string") return info;

  try {
    return JSON.parse(info) as Record<string, string>;
  } catch (error) {
    if (error instanceof SyntaxError) console.error("Unparsable JSON", error.message, info);
  }

  return {};
}

export const transformBatch = (batch: BatchServerResponse): Batch => ({
  ...snakeToCamel(batch),
  confirmed: false,
  info: parseInfo(batch.info),
  result:
    typeof batch.result_metadata === "string" ? JSON.parse(batch.result_metadata || "{}") : batch.result_metadata || {}
});

export const transformBatches = (batches: BatchServerResponse[]): Batch[] =>
  (batches || []).map((batch) => transformBatch(batch));

const useBatches = ({
  themeId,
  conceptId,
  isCompleted,
  ids,
  brandId,
  type,
  creatorId,
  variationSetIds,
  adAssetGroupIds,
  pollingIntervalMillis
}: UseBatchesProps) => {
  const params = useMemo(() => {
    const p = new URLSearchParams();
    const addParam = (key: string, value: string | number | boolean | undefined) => {
      if (value !== undefined) {
        p.append(key, value.toString());
      }
    };

    addParam("theme_id", themeId);
    addParam("is_completed", isCompleted);
    addParam("concept_id", conceptId);
    addParam("brand_id", brandId);
    addParam("type", type);
    addParam("creator_id", creatorId);

    variationSetIds?.forEach((id) => addParam("variation_set_ids", id));
    adAssetGroupIds?.forEach((id) => addParam("ad_asset_group_ids", id));
    ids?.forEach((id) => addParam("ids", id));

    return p;
  }, [themeId, conceptId, isCompleted, ids, brandId, type, creatorId, variationSetIds, adAssetGroupIds]);

  const queryString = params.toString();
  const url = `${RelativeUrl}/${BatchUrl}${queryString && `?${queryString}`}`;

  const getKey = useMemo(() => {
    // returning a null value prevents SWR from fetching
    if (conceptId === 0) return;
    if (ids && ids.length === 0) return;
    if (variationSetIds && variationSetIds.length === 0) return;
    if (adAssetGroupIds && adAssetGroupIds.length === 0) return;
    return url;
  }, [conceptId, ids, url, variationSetIds, adAssetGroupIds]);

  const {
    data,
    error,
    isLoading,
    mutate: invalidateBatchCache
  } = useSWR(getKey, fetcher<BatchServerResponse[]>, {
    refreshInterval: pollingIntervalMillis || 0
  });

  const batches = useMemo(() => {
    return data ? transformBatches(data) : [];
  }, [data]);

  return {
    batches,
    error,
    isLoading,
    invalidateBatchCache
  };
};

export default useBatches;
